import type { ExternalNetworkEntityKind } from '@terra/shared-contracts-external-network';
import type { EnetAssetListCellValues } from './asset-list-cell-values';
import {
  domainResolvesTo,
  firstHostname,
  readBooleanField,
  readNumberField,
  readStringField,
  softwareDetectionSource,
} from './derive-asset-list-fields';
import {
  getNetblockWhoisCountry,
  getNetblockWhoisOrganization,
} from '@terra/ui-design-system';
import { readNetblockWhois } from './netblock-whois.utils';
import { readHostnames } from './read-external-asset-row-fields';

export function buildExternalAssetListCells({
  entityKind,
  raw,
  typeLabel,
  typeColorPalette,
  scopeCells,
  formatSource,
  tlsLabel,
  topLevelLabel,
  subdomainLevelLabel,
  publicAccessLabel,
}: {
  entityKind: ExternalNetworkEntityKind;
  raw: Record<string, unknown>;
  typeLabel?: string;
  typeColorPalette?: string;
  scopeCells: (args: {
    scope?: string | null;
    effectiveScope?: string | null;
  }) => Pick<
    EnetAssetListCellValues,
    'scope' | 'scopeLabel' | 'scopeInherited'
  >;
  formatSource: (args: { sourceType?: string | null }) => string | null;
  tlsLabel: string;
  topLevelLabel: string;
  subdomainLevelLabel: string;
  publicAccessLabel: (access?: string | null) => string | null;
}): EnetAssetListCellValues {
  const typeCells = { typeLabel, typeColorPalette };
  const whois = readNetblockWhois({ raw });
  const dnsRecords = Array.isArray(raw.dnsRecords)
    ? raw.dnsRecords.filter(
        (record): record is { rrType?: string; rdata?: string } =>
          Boolean(record) && typeof record === 'object',
      )
    : [];
  const observations = Array.isArray(raw.observations)
    ? raw.observations.filter(
        (observation): observation is { sourceType?: string | null } =>
          Boolean(observation) && typeof observation === 'object',
      )
    : [];

  switch (entityKind) {
    case 'NETBLOCK':
      return {
        ...typeCells,
        attribution:
          readStringField({ raw, key: 'cloudProvider' }) ??
          getNetblockWhoisOrganization({ whois }),
        ip: readStringField({ raw, key: 'cidr' }),
        registrant: getNetblockWhoisOrganization({ whois }),
        country: getNetblockWhoisCountry({ whois }),
        hostsCount: readNumberField({ raw, key: 'hostCount' }),
        source: formatSource({
          sourceType: readStringField({ raw, key: 'sourceType' }),
        }),
        ...scopeCells({
          scope: readStringField({ raw, key: 'scope' }),
          effectiveScope: readStringField({ raw, key: 'effectiveScope' }),
        }),
      };
    case 'DOMAIN':
      return {
        ...typeCells,
        attribution: readStringField({ raw, key: 'resolvedCloudProvider' }),
        level: raw.isTopLevel === true ? topLevelLabel : subdomainLevelLabel,
        subdomainsCount: readNumberField({ raw, key: 'subdomainCount' }),
        dnsRecordsCount: dnsRecords.length,
        resolvesTo: domainResolvesTo({ dnsRecords }),
        registrar: readStringField({ raw, key: 'registrar' }),
        source: formatSource({
          sourceType: readStringField({ raw, key: 'sourceType' }),
        }),
        ...scopeCells({
          scope: readStringField({ raw, key: 'scope' }),
          effectiveScope: readStringField({ raw, key: 'effectiveScope' }),
        }),
      };
    case 'HOST':
      return {
        ...typeCells,
        attribution: readStringField({ raw, key: 'cloudProvider' }),
        reverseDns: firstHostname({ hostnames: readHostnames({ raw }) }),
        netblock: readStringField({ raw, key: 'netblockCidr' }),
        provider: readStringField({ raw, key: 'cloudProvider' }),
        source: formatSource({
          sourceType: readStringField({ raw, key: 'sourceType' }),
        }),
      };
    case 'GATEWAY':
      return {
        ...typeCells,
        attribution:
          readStringField({ raw, key: 'cloudProvider' }) ??
          readStringField({ raw, key: 'provider' }),
        provider: readStringField({ raw, key: 'provider' }),
        functionLabel: formatSource({
          sourceType: readStringField({ raw, key: 'providerCategory' }),
        }),
        domainsCount: readNumberField({ raw, key: 'resolvedDomainCount' }),
        servicesCount: readNumberField({ raw, key: 'serviceCount' }),
        source: formatSource({
          sourceType: readStringField({ raw, key: 'sourceType' }),
        }),
        ...scopeCells({
          scope: readStringField({ raw, key: 'scope' }),
        }),
      };
    case 'NETWORK_SERVICE':
      return {
        ...typeCells,
        transport: readStringField({ raw, key: 'transportProtocol' }),
        host:
          readStringField({ raw, key: 'hostIp' }) ??
          readStringField({ raw, key: 'gatewayHostname' }),
        tlsEnabled: readBooleanField({ raw, key: 'tlsEnabled' }),
        tlsLabel,
        source: formatSource({
          sourceType: readStringField({ raw, key: 'sourceType' }),
        }),
        ...scopeCells({
          scope: readStringField({ raw, key: 'scope' }),
          effectiveScope: readStringField({ raw, key: 'effectiveScope' }),
        }),
      };
    case 'SOFTWARE':
      return {
        ...typeCells,
        attribution: readStringField({ raw, key: 'vendor' }),
        domain: readStringField({ raw, key: 'domainName' }),
        vendor: readStringField({ raw, key: 'vendor' }),
        category: formatSource({
          sourceType: readStringField({ raw, key: 'softwareType' }),
        }),
        detection: formatSource({
          sourceType: softwareDetectionSource({ observations }),
        }),
      };
    case 'STORAGE_SERVICE':
      return {
        ...typeCells,
        attribution: readStringField({ raw, key: 'provider' }),
        provider: readStringField({ raw, key: 'provider' }),
        access: publicAccessLabel(
          readStringField({ raw, key: 'publicAccess' }),
        ),
        source: formatSource({
          sourceType: readStringField({ raw, key: 'sourceType' }),
        }),
        ...scopeCells({
          scope: readStringField({ raw, key: 'scope' }),
        }),
      };
  }
}
